import { useMemo } from 'react';
import { atom, reatomAsync, withAbort, withErrorAtom } from '@reatom/framework';
import { useCtx } from '@reatom/npm-react';
import { getComponentDebugName } from '@/js/libs/devtools';
import { createObservableFromAtom } from '@/js/libs/observable';
import { parseError } from './parseError';
import { useRequestContextValue } from './UseRequestContext';
export function createLazyFetch(apiMethod, config, _ctx) {
    const { ignoreErrors, name = `${apiMethod.name}Lazy` } = config;
    const fetchAction = reatomAsync(async (ctx, params) => {
        const response = await ctx.schedule(() => apiMethod({ params, signal: ctx.controller.signal, ignoreErrors: ctx.get(ignoreErrors) }));
        ctx.get(config.onSuccess)?.(response.data, params, response.status);
        return response;
    }, name).pipe(withAbort({ strategy: _ctx.get(config.cancelPreviousRequests) ? 'last-in-win' : 'none' }), withErrorAtom((ctx, e) => {
        const error = parseError(e, name);
        // safety call `onFail` before render
        if (error)
            ctx.schedule(() => ctx.get(config.onFail)?.(error));
        return error;
    }));
    fetchAction.pendingAtom.__reatom.name = fetchAction.pendingAtom.__reatom.name?.replace('pendingAtom', '_pendingAtom');
    const isLoadingAtom = atom((ctx) => ctx.spy(fetchAction.pendingAtom) > 0, `${name}.isLoadingAtom`);
    return {
        error$: createObservableFromAtom(fetchAction.errorAtom),
        pending$: createObservableFromAtom(isLoadingAtom),
        load: (params) => fetchAction(_ctx, params).then((response) => response.data),
        abort: () => fetchAction.abort(_ctx),
    };
}
export function useLazyRequest(props) {
    const { onFail: onFailFromContext } = useRequestContextValue();
    const { apiMethod, cancelPreviousRequests = true, onFail = onFailFromContext, onSuccess, ignoreErrors } = props;
    const ctx = useCtx();
    const name = getComponentDebugName(`${apiMethod.name}Lazy`);
    const config = useMemo(() => ({
        cancelPreviousRequests: atom(cancelPreviousRequests, `${name}._cancelPreviousRequests`),
        onFail: atom(undefined, `${name}._onFail`),
        onSuccess: atom(undefined, `${name}._onSuccess`),
        ignoreErrors: atom(ignoreErrors ?? null),
        name,
    }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    if (ctx.get(config.onFail) !== onFail) {
        config.onFail(ctx, () => onFail);
    }
    if (ctx.get(config.onSuccess) !== onSuccess) {
        config.onSuccess(ctx, () => onSuccess);
    }
    if (ctx.get(config.cancelPreviousRequests) !== cancelPreviousRequests) {
        config.cancelPreviousRequests(ctx, () => cancelPreviousRequests);
    }
    return useMemo(() => createLazyFetch(apiMethod, config, ctx), [apiMethod, config, ctx]);
}
